import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import {
  DuplicateIcon,
  EditIcon,
  FundraisingIcon,
  LiquidityIcon,
  ThreeDotsIcon,
  TrashBinIcon,
} from '../../assets/icons/index';
import { AlertDialogWrapper } from '../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../components/popover/index';
import { Tooltip } from '../../components/tooltip/index';
import { QueryKey } from '../../constants/index';
import {
  ScenarioHooks,
  useFormat,
  useInvalidateQueries,
  useLocale,
  useModalState,
} from '../../hooks/index';
import { PageRoute } from '../../types/page.types';
import { Scenario, ScenarioType, ScenarioTypeTitle } from '../../types/scenario-modeling.types';
import { DeleteModal, RenameModal } from './modals/index';
import { useScenarioStore } from './scenario-modeling.store';

export type ScenarioCardProps = {
  isMobile: boolean;
  scenario: Scenario;
  scenarioNames?: string[];
};

export const ScenarioCard: FC<ScenarioCardProps> = ({ scenario, scenarioNames, isMobile }) => {
  const { format } = useFormat();
  const { messagesLocale } = useLocale();
  const { id, name, creator, type, valuation, updatedAt, currentValuation } = scenario;

  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_SCENARIOS);
  const navigate = useNavigate();

  const { update, isPending } = ScenarioHooks.useUpdate();
  const { duplicate } = ScenarioHooks.useDuplicate();
  const { deleteScenario, isPending: deleteIsPending } = ScenarioHooks.useDelete();
  const { setScenarioId } = useScenarioStore();

  const {
    toggler: renameModalToggler,
    isOpen: isOpenRenameModal,
    handleOpenModal: handleOpenRenameModal,
    handleCloseModal: handleCloseRenameModal,
    handleSuccessModal: handleSuccessRenameModal,
    modalData: currentName,
  } = useModalState<string | undefined, unknown, string>({
    onSuccess: (name?: string) => {
      update(
        {
          scenarioId: id,
          data: {
            name,
          },
        },
        {
          onSuccess: () => {
            toast.success(messagesLocale['SCENARIO_RENAMED_SUCCESSFULLY']);
            invalidateQuery();
          },
        },
      );
    },
  });

  const {
    handleCloseModal: handleCloseDeleteModal,
    handleSuccessModal: handleSuccessDeleteModal,
    handleOpenModal: handleOpenDeleteModal,
    isOpen: isOpenDeleteModal,
    toggler: deleteModalToggler,
  } = useModalState({
    onSuccess: () => {
      deleteScenario(
        {
          scenarioId: id,
        },
        {
          onSuccess: () => {
            toast.success(messagesLocale['SCENARIO_DELETED_SUCCESSFULLY']);
            setScenarioId('');
            invalidateQuery();
          },
        },
      );
    },
  });

  const handleDuplicate = (id: string) => {
    duplicate(
      { scenarioId: id },
      {
        onSuccess: () => {
          toast.success(messagesLocale['SCENARIO_DUPLICATED_SUCCESSFULLY']);
          invalidateQuery();
        },
      },
    );
  };

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: renameModalToggler, open: isOpenRenameModal }}>
        <RenameModal
          currentName={currentName}
          isPending={isPending}
          onClose={handleCloseRenameModal}
          onSuccess={handleSuccessRenameModal}
          scenarioNames={scenarioNames}
        />
      </AlertDialogWrapper>
      <AlertDialogWrapper control={{ onOpenChange: deleteModalToggler, open: isOpenDeleteModal }}>
        <DeleteModal
          isPending={deleteIsPending}
          onClose={handleCloseDeleteModal}
          onSuccess={handleSuccessDeleteModal}
        />
      </AlertDialogWrapper>

      <div className="flex w-full flex-col rounded-lg shadow-sm xlg:max-w-[286px] lg:max-w-[404px]">
        <div
          className={twMerge(
            'h-4 w-full rounded-t-lg',
            type === ScenarioType.FUNDRAISING ? 'bg-forest-25' : 'bg-sunbeam-50',
          )}
        />
        <div className="flex flex-col gap-[28px] p-4">
          <div
            className="flex cursor-pointer flex-col gap-2"
            onClick={() => {
              setScenarioId(id);
              navigate(`${PageRoute.SCENARIO_MODELING}/${id}`, {
                state: { fromClick: true },
              });
            }}
          >
            <div className="flex items-center gap-2">
              {type === ScenarioType.FUNDRAISING ? <FundraisingIcon /> : <LiquidityIcon />}
              <span className="text-label-sm font-medium text-gray-500">
                <AppFormattedMessage id={ScenarioTypeTitle[type]} />
              </span>
            </div>
            <span className="max-w-full truncate text-xl font-semibold text-black">{name}</span>
            <div className="flex items-center justify-between gap-2">
              <span className="text-nowrap text-label-md font-medium text-gray-600">
                <AppFormattedMessage id="CAP_TABLE" />
              </span>
              <span className="truncate rounded-2xl bg-gray-50 px-2 py-[2px] text-label-md font-medium text-gray-500">
                {currentValuation
                  ? `${messagesLocale['LATEST']} - ${format(updatedAt, 'dd/MM/yyyy')}`
                  : valuation?.name}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-[450] text-gray-700">
                {creator?.fullName[0]}
              </div>
              <div className="flex max-w-[200px] flex-col">
                <Tooltip childrenWrapperClassName="max-w-[200px]" content={creator?.fullName}>
                  <span className="truncate text-xs font-[450] text-gray-700">
                    {creator?.fullName}
                  </span>
                </Tooltip>

                <span className="text-label-sm font-medium text-gray-400">
                  <AppFormattedMessage id="LAST_GENERATED" />:{' '}
                  {updatedAt ? format(updatedAt, 'dd/MM/yyyy') : '-'}
                </span>
              </div>
            </div>
            {!isMobile && (
              <div className="flex cursor-pointer items-center justify-center">
                <Popover>
                  <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
                    <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                      <ThreeDotsIcon />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
                    <PopoverClose>
                      <div
                        className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => handleOpenRenameModal(name)}
                      >
                        <EditIcon className="size-4" />
                        <span className="text-sm font-normal text-gray-700">
                          <AppFormattedMessage id="RENAME" />
                        </span>
                      </div>
                      <div
                        className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => handleDuplicate(id)}
                      >
                        <DuplicateIcon />
                        <span className="text-sm font-normal text-gray-700">
                          <AppFormattedMessage id="DUPLICATE" />
                        </span>
                      </div>
                      <div
                        className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-fireside-500 hover:bg-gray-50"
                        data-permission-required
                        onClick={handleOpenDeleteModal}
                      >
                        <TrashBinIcon className="size-4" iconColor="#F04438" />
                        <span className="text-sm font-normal text-fireside-500">
                          <AppFormattedMessage id="DELETE" />
                        </span>
                      </div>
                    </PopoverClose>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
